import * as THREE from "three";
import { VRButton } from "three/examples/jsm/webxr/VRButton.js";
import AppData from "../../app.data";
import PhysicsService from "../../services/physics/physics.service";
import SoundService from "../../services/sound/sound.service";
import { EventRaycaster } from "../../services/event/handlers/raycast.handler";
import { VRPerformanceMonitor } from "./vr.performance";
import { VRViewerManager } from "./vr.viewer.manager";
import { VRMetadataPanelManager } from "./vr.metadata.panel";
import { installXRFrameDetectedPlanesGuard } from "../xr.frame.guard";
import { getConfiguredRuntimeCameraHeight } from "../../../modals/render.settings.camera";
import type {
  VRInteractionMode,
  VRLocomotionMode,
  VRSessionState,
  VRViewerMedia,
} from "./vr.types";

const appData = new AppData();

const SNAP_TURN_COOLDOWN_MS = 250;
const AXIS_DEADZONE = 0.2;
const CONTROLLER_RAY_LENGTH = 12;
const METADATA_PANEL_SCROLL_SPEED = 520;
const TELEPORT_UP_NORMAL_DOT_MIN = 0.65;
const TELEPORT_MAX_SURFACE_DROP = 1.85;
const TELEPORT_MAX_SURFACE_RISE = 3;
const TELEPORT_BLOCKER_DISTANCE_EPSILON = 0.05;
const TELEPORT_MAX_TARGET_DISTANCE = 18;
const TELEPORT_PROJECTED_TARGET_DISTANCE = 4;
const TELEPORT_DOWNCAST_HEIGHT = 5;
const TELEPORT_DOWNCAST_DEPTH = 10;
const TELEPORT_FALLBACK_DISTANCES = [1.5, 2.5, 4, 6, 8, 10, 12, 15, 18];
const EXIT_VR_BUTTON_FORWARD_OFFSET = 0.62;
const EXIT_VR_BUTTON_FLOOR_OFFSET = 0.018;
const EXIT_VR_BUTTON_WIDTH = 0.46;
const EXIT_VR_BUTTON_HEIGHT = 0.16;
const VR_OPTIONAL_FEATURES = ["local-floor", "bounded-floor"];
const VR_BUTTON_ISOLATED_EVENTS = [
  "mousedown",
  "pointerdown",
  "touchstart",
];
const VR_BUTTON_ACTIVATION_EVENTS = ["mouseup", "pointerup", "touchend"];
const VR_BUTTON_ACTIVATION_DEBOUNCE_MS = 350;
const WORLD_UP = new THREE.Vector3(0, 1, 0);

type ControllerSlot = {
  controller: THREE.Group;
  ray: THREE.Line;
  focusCue: THREE.Mesh;
  inputSource: XRInputSource | null;
  selectPressed: boolean;
  squeezePressed: boolean;
  gamepadSqueezePressed: boolean;
  lastSnapAt: number;
  lastWorldPosition: THREE.Vector3;
};

const createControllerRay = () => {
  const geometry = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(0, 0, -CONTROLLER_RAY_LENGTH),
  ]);
  const material = new THREE.LineBasicMaterial({ color: 0x10b981, transparent: true, opacity: 0.9 });
  const line = new THREE.Line(geometry, material);
  line.name = "vr-controller-ray";
  line.visible = false;
  return line;
};

const createControllerFocusCue = () => {
  const geometry = new THREE.SphereGeometry(0.025, 20, 20);
  const material = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.95,
    depthTest: false,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = "VR_Controller_Focus_Cue";
  mesh.renderOrder = 999;
  mesh.visible = false;
  return mesh;
};

const createReticle = () => {
  const geometry = new THREE.RingGeometry(0.01, 0.02, 24);
  const material = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.95,
    side: THREE.DoubleSide,
    depthTest: false,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = "VR_Reticle";
  mesh.renderOrder = 999;
  return mesh;
};

const createTeleportMarker = () => {
  const geometry = new THREE.RingGeometry(0.1, 0.16, 32);
  const material = new THREE.MeshBasicMaterial({
    color: 0x10b981,
    transparent: true,
    opacity: 0.8,
    side: THREE.DoubleSide,
  });
  const marker = new THREE.Mesh(geometry, material);
  marker.rotation.x = -Math.PI / 2;
  marker.visible = false;
  marker.name = "VR_Teleport_Marker";
  return marker;
};

const createExitVRButtonTexture = () => {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 180;
  const context = canvas.getContext("2d");
  if (!context) {
    return null;
  }

  context.fillStyle = "rgba(45, 51, 57, 0.42)";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.strokeStyle = "rgba(255, 255, 255, 0.55)";
  context.lineWidth = 5;
  context.strokeRect(4, 4, canvas.width - 8, canvas.height - 8);
  context.fillStyle = "rgba(255, 255, 255, 0.92)";
  context.font = "bold 48px sans-serif";
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText("Exit VR", canvas.width / 2, canvas.height / 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
};

const createExitVRButton = () => {
  const geometry = new THREE.PlaneGeometry(EXIT_VR_BUTTON_WIDTH, EXIT_VR_BUTTON_HEIGHT);
  const texture = createExitVRButtonTexture();
  const materialOptions: THREE.MeshBasicMaterialParameters = {
    color: 0xffffff,
    transparent: true,
    opacity: 0.74,
    side: THREE.DoubleSide,
    depthTest: true,
    depthWrite: false,
  };
  if (texture) {
    materialOptions.map = texture;
  }
  const material = new THREE.MeshBasicMaterial(materialOptions);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = "VR_Exit_Button";
  mesh.renderOrder = 1000;
  mesh.visible = false;
  mesh.userData.isVRSystemControl = true;
  mesh.userData.vrSystemAction = "exit-vr";
  return mesh;
};

const resolveHorizontalForward = (
  forward: THREE.Vector3,
  cameraQuaternion: THREE.Quaternion
) => {
  const horizontalForward = forward.clone();
  horizontalForward.y = 0;
  if (horizontalForward.lengthSq() >= 0.0001) {
    return horizontalForward.normalize();
  }

  const right = new THREE.Vector3(1, 0, 0).applyQuaternion(cameraQuaternion);
  right.y = 0;
  if (right.lengthSq() >= 0.0001) {
    return new THREE.Vector3().crossVectors(WORLD_UP, right.normalize()).normalize();
  }

  return new THREE.Vector3(0, 0, -1);
};

export class VRManager {
  renderer: THREE.WebGLRenderer;
  canvas: HTMLElement;
  vrRig: THREE.Group | null = null;
  desktopCameraState: any = null;
  physicsService: PhysicsService;
  stats: any;
  controls: any;
  vrButton: HTMLElement | null = null;
  sessionStartHandler: any;
  sessionEndHandler: any;
  private sessionSelectHandler: ((event: XRInputSourceEvent) => void) | null = null;
  private listenersBound = false;
  private controllers: ControllerSlot[] = [];
  private raycasterHandler: EventRaycaster | null = null;
  private soundService: SoundService | null = null;
  private reticle: THREE.Mesh | null = null;
  private teleportMarker: THREE.Mesh | null = null;
  private exitVRButton: THREE.Mesh | null = null;
  private viewerManager: VRViewerManager | null = null;
  private metadataPanelManager: VRMetadataPanelManager | null = null;
  private performanceMonitor = new VRPerformanceMonitor();
  private sessionEventCallback: (state: VRSessionState) => void = () => {};
  private failureEventCallback: (reason: string) => void = () => {};
  private sessionState: VRSessionState = {
    availability: "unsupported",
    runtime: "desktop",
    isPresenting: false,
    interactionMode: "reticle",
    locomotionMode: "stationary",
    viewer: {
      kind: null,
      status: "idle",
      interactionEnabled: false,
      error: null,
    },
    hasControllers: false,
    hasMovementAxes: false,
    teleportEnabled: false,
    qualityProfile: "vr-safe",
  };
  private lastTeleportTarget: THREE.Vector3 | null = null;
  private currentTeleportSurfaceY: number | null = null;
  private teleportBlockersCache: THREE.Object3D[] = [];
  private teleportBlockersNavmeshRoot: THREE.Object3D | null = null;
  private teleportBlockersDirty = true;
  private pendingViewerSourceObject: THREE.Object3D | null = null;
  private hiddenViewerSourceObject: { object: THREE.Object3D; visible: boolean } | null = null;

  // Callback to be set by main renderer
  onEnterVRCallback: () => void = () => {};
  onExitVRCallback: () => void = () => {};

  constructor(renderer: THREE.WebGLRenderer, canvas: HTMLElement, physicsService: PhysicsService) {
    this.renderer = renderer;
    this.canvas = canvas;
    this.physicsService = physicsService;
  }

  public setRaycaster(raycasterHandler: EventRaycaster | null) {
    this.raycasterHandler = raycasterHandler;
  }

  public setSoundService(soundService: SoundService | null) {
    this.soundService = soundService;
  }

  public setVRSessionEvent(method: (state: VRSessionState) => void) {
    this.sessionEventCallback = method;
  }

  public setVRFailureEvent(method: (reason: string) => void) {
    this.failureEventCallback = method;
  }

  public getVRSessionState(): VRSessionState {
    return {
      ...this.sessionState,
      viewer: { ...this.sessionState.viewer },
    };
  }

  public isVRPresenting() {
    return Boolean(this.renderer?.xr?.isPresenting);
  }

  public async endVRSession() {
    const session = this.renderer?.xr?.getSession?.();
    if (!session) {
      return;
    }

    if (!this.transitionRuntime("exiting")) {
      return;
    }

    try {
      await session.end();
    } catch (error) {
      console.warn("VRManager: failed to end XR session", error);
      this.failRuntime("Failed to exit VR session");
    }
  }

  public async openViewer(media: VRViewerMedia) {
    if (!this.isVRPresenting() || !this.viewerManager) {
      return;
    }

    if (!this.transitionRuntime("viewer")) {
      return;
    }

    this.metadataPanelManager?.close();
    this.hidePendingViewerSourceObject();
    const viewerState = await this.viewerManager.open(media, this.sessionState.interactionMode);
    if (viewerState.status === "error") {
      this.restoreHiddenViewerSourceObject();
      this.failViewer(viewerState.error || "Viewer unavailable");
      return;
    }

    this.sessionState.viewer = viewerState;
    this.sessionState.locomotionMode = "stationary";
    this.sessionState.teleportEnabled = false;
    this.notifySessionState();
  }

  public closeViewer() {
    this.viewerManager?.close();
    this.restoreHiddenViewerSourceObject();
    if (this.isVRPresenting()) {
      this.transitionRuntime("browse");
      this.sessionState.viewer = this.viewerManager?.getState() || {
        kind: null,
        status: "idle",
        interactionEnabled: false,
        error: null,
      };
      this.syncCapabilities();
      this.notifySessionState();
    }
  }

  public async checkAndCreateVRButton() {
    if (this.vrButton) {
      return;
    }

    installXRFrameDetectedPlanesGuard();

    const xr = (navigator as any).xr;
    if (!xr) {
      this.sessionState.availability = "unsupported";
      this.notifySessionState();
      return;
    }

    try {
      const isVRSupported = await xr.isSessionSupported("immersive-vr");
      this.sessionState.availability = isVRSupported ? "supported" : "unsupported";
      this.notifySessionState();
      if (!isVRSupported) {
        return;
      }

      this.vrButton = VRButton.createButton(this.renderer, {
        optionalFeatures: VR_OPTIONAL_FEATURES,
      });
      this.styleVRButton(this.vrButton);
      this.isolateVRButtonEvents(this.vrButton);
      this.canvas.appendChild(this.vrButton);
      this.setupVREventListeners();
    } catch (error) {
      console.log("Error checking VR support:", error);
      this.sessionState.availability = "unsupported";
      this.notifySessionState();
    }
  }

  private styleVRButton(button: HTMLElement) {
    if (!button.style.position) {
      button.style.position = "absolute";
    }
    if (!button.style.left) {
      button.style.left = "50%";
    }
    if (!button.style.transform) {
      button.style.transform = "translateX(-50%)";
    }
    if (!button.style.padding) {
      button.style.padding = "12px 18px";
    }
    if (!button.style.border) {
      button.style.border = "1px solid rgba(255, 255, 255, 0.55)";
    }
    if (!button.style.borderRadius) {
      button.style.borderRadius = "4px";
    }
    if (!button.style.background) {
      button.style.background = "rgba(45, 51, 57, 0.92)";
    }
    if (!button.style.color) {
      button.style.color = "#ffffff";
    }
    button.style.bottom = "65px";
    button.style.zIndex = "10000";
    button.style.pointerEvents = "auto";
  }

  private setupVREventListeners() {
    if (this.listenersBound) {
      return;
    }

    this.sessionStartHandler = () => {
      this.transitionRuntime("entering");
      if (this.vrButton) {
        this.vrButton.textContent = "EXIT VR";
      }
      if (this.controls) this.controls.enabled = false;
      this.onEnterVR();
    };

    this.sessionEndHandler = () => {
      if (this.vrButton) {
        this.vrButton.textContent = "ENTER VR";
      }
      if (this.controls) this.controls.enabled = true;
      this.onExitVR();
    };

    this.renderer.xr.addEventListener("sessionstart", this.sessionStartHandler);
    this.renderer.xr.addEventListener("sessionend", this.sessionEndHandler);
    this.listenersBound = true;
  }

  private isolateVRButtonEvents(button: HTMLElement) {
    let lastActivationAt = 0;
    let suppressTrustedClickUntil = 0;

    const stopAtButton = (event: Event) => {
      event.stopPropagation();
    };

    VR_BUTTON_ISOLATED_EVENTS.forEach((eventName) => {
      button.addEventListener(eventName, stopAtButton, true);
    });

    const activateAtButton = (event: Event) => {
      event.stopPropagation();

      if (this.renderer?.xr?.isPresenting || this.renderer?.xr?.getSession?.()) {
        return;
      }

      const now = Date.now();
      if (now - lastActivationAt < VR_BUTTON_ACTIVATION_DEBOUNCE_MS) {
        return;
      }

      lastActivationAt = now;
      suppressTrustedClickUntil = now + VR_BUTTON_ACTIVATION_DEBOUNCE_MS;
      const clickHandler = (button as HTMLButtonElement).onclick;
      if (typeof clickHandler === "function") {
        const result = clickHandler.call(button, event as MouseEvent);
        if (result && typeof (result as Promise<unknown>).catch === "function") {
          (result as Promise<unknown>).catch((error) => {
            if (this.renderer?.xr?.getSession?.() && (error as DOMException)?.name === "InvalidStateError") {
              return;
            }
            console.warn("VRManager: VR button activation failed", error);
          });
        }
        return;
      }

      button.click();
    };

    const suppressDuplicateClick = (event: MouseEvent) => {
      if (event.isTrusted && Date.now() <= suppressTrustedClickUntil) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };

    VR_BUTTON_ACTIVATION_EVENTS.forEach((eventName) => {
      button.addEventListener(eventName, activateAtButton, true);
    });
    button.addEventListener("click", suppressDuplicateClick, true);
  }

  public setControls(controls: any) {
    this.controls = controls;
  }

  public setStats(stats: any) {
    this.stats = stats;
  }

  public update(_delta: number) {
    if (!this.renderer?.xr?.isPresenting) {
      return;
    }

    this.syncCapabilities();
    this.syncControllerButtonStates();
    this.updateReticle();
    this.updateExitVRButton();
    this.updateControllerFocusCues();
    this.updateTeleportTarget();

    const performanceSnapshot = this.performanceMonitor.sample(
      Math.max(this.renderer.info?.render?.frame ? 0 : 0, (_delta || 0) * 1000),
      this.sessionState.viewer.kind,
      this.sessionState.locomotionMode
    );
    if (performanceSnapshot.shouldDegradeQuality) {
      appData.renderService?.applyVRQualityProfile?.("vr-degraded");
      this.sessionState.qualityProfile = "vr-degraded";
    }

    if (this.sessionState.runtime === "viewer" && this.viewerManager) {
      this.viewerManager.update(_delta, {
        interactionMode: this.sessionState.interactionMode,
        scaleInput: this.getScaleInput(),
      });
      this.sessionState.viewer = this.viewerManager.getState();
      this.notifySessionState();
      return;
    }

    if (this.sessionState.runtime !== "browse" || !this.sessionState.hasControllers) {
      this.notifySessionState();
      return;
    }

    const metadataPanelConsumedStick = this.handleMetadataPanelScroll(_delta || 0);
    if (!metadataPanelConsumedStick) {
      this.handleMovement();
    }
    this.handleSnapTurn();
    this.notifySessionState();
  }

  private onEnterVR() {
    this.syncVRCameraWithDesktopCamera();
    this.ensureReticle();
    this.ensureTeleportMarker();
    this.ensureExitVRButton();
    this.ensureControllers();
    this.ensureSessionSelectListener();
    this.performanceMonitor.reset();
    this.currentTeleportSurfaceY = this.resolveTeleportSurfaceYFromCurrentPose();
    this.markTeleportBlockersDirty();
    this.viewerManager = this.soundService
      ? new VRViewerManager(appData.spaceScene, appData.camera, this.soundService, (reason) => {
          this.failViewer(reason);
        })
      : null;
    this.metadataPanelManager = new VRMetadataPanelManager(appData.spaceScene, appData.camera);

    if (this.stats) {
      this.stats.dom.style.display = "none";
    }

    this.transitionRuntime("browse");
    this.syncCapabilities();
    this.onEnterVRCallback();
    this.notifySessionState();
  }

  private syncVRCameraWithDesktopCamera() {
    if (!appData.camera || !appData.spaceScene) return;

    const desktopPosition = appData.camera.position.clone();
    const desktopRotation = appData.camera.rotation.clone();
    this.desktopCameraState = {
      position: desktopPosition,
      rotation: desktopRotation,
    };

    if (!this.vrRig) {
      this.vrRig = new THREE.Group();
      this.vrRig.name = "VR_Scene_Offset";

      const originalChildren = [...appData.spaceScene.children];
      originalChildren.forEach((child) => {
        if (child !== this.vrRig) {
          this.vrRig?.add(child);
        }
      });

      appData.spaceScene.add(this.vrRig);
    }

    this.vrRig.position.copy(desktopPosition.clone().negate());
    this.vrRig.rotation.set(0, -desktopRotation.y, 0);
    appData.camera.position.set(0, 0, 0);
    appData.camera.rotation.set(0, 0, 0);

    if (this.physicsService?.playerCollider) {
      const cameraHeight = getConfiguredRuntimeCameraHeight(appData);
      if (typeof this.physicsService.setPlayerColliderHeadPosition === "function") {
        this.physicsService.setPlayerColliderHeadPosition(desktopPosition, cameraHeight);
      } else {
        this.physicsService.playerCollider.end.copy(desktopPosition);
        this.physicsService.playerCollider.start.copy(desktopPosition);
        this.physicsService.playerCollider.start.y -= cameraHeight;
      }
    }
  }

  private onExitVR() {
    this.viewerManager?.close();
    this.viewerManager = null;
    this.metadataPanelManager?.close();
    this.metadataPanelManager = null;
    this.restoreHiddenViewerSourceObject();
    this.pendingViewerSourceObject = null;
    this.removeReticle();
    this.removeExitVRButton();
    this.hideControllerFocusCues();
    this.hideTeleportMarker();
    this.lastTeleportTarget = null;
    this.currentTeleportSurfaceY = null;
    this.clearTeleportBlockersCache();
    this.detachControllers();
    this.restoreSceneFromRig();

    if (this.stats) {
      this.stats.dom.style.display = "block";
    }

    this.transitionRuntime("desktop");
    this.sessionState.viewer = {
      kind: null,
      status: "idle",
      interactionEnabled: false,
      error: null,
    };
    this.sessionState.qualityProfile = "vr-safe";
    this.performanceMonitor.reset();
    this.cleanupSessionSelectListener();
    this.onExitVRCallback();
    this.notifySessionState();
  }

  private restoreSceneFromRig() {
    if (!this.vrRig || !appData.spaceScene) {
      return;
    }

    const vrRigChildren = [...this.vrRig.children];
    vrRigChildren.forEach((child) => {
      appData.spaceScene.add(child);
    });

    appData.spaceScene.remove(this.vrRig);
    this.vrRig = null;

    if (this.desktopCameraState) {
      appData.camera.position.copy(this.desktopCameraState.position);
      appData.camera.rotation.copy(this.desktopCameraState.rotation);

      if (this.physicsService?.playerCollider) {
        this.physicsService.playerCollider.end.copy(this.desktopCameraState.position);
        this.physicsService.playerCollider.start.copy(this.desktopCameraState.position);
        this.physicsService.playerCollider.start.y = this.desktopCameraState.position.y;
        this.physicsService.playerCollider.end.y = this.desktopCameraState.position.y;
      }
    }
  }

  private ensureControllers() {
    if (this.controllers.length === 0) {
      for (let index = 0; index < 2; index++) {
        const controller = this.renderer.xr.getController(index);
        const ray = createControllerRay();
        const focusCue = createControllerFocusCue();
        controller.add(ray);
        const slot: ControllerSlot = {
          controller,
          ray,
          focusCue,
          inputSource: null,
          selectPressed: false,
          squeezePressed: false,
          gamepadSqueezePressed: false,
          lastSnapAt: 0,
          lastWorldPosition: new THREE.Vector3(),
        };

        controller.addEventListener("connected", (event: any) => {
          slot.inputSource = event.data || null;
          slot.ray.visible = this.sessionState.interactionMode === "controller";
          this.syncCapabilities();
        });
        controller.addEventListener("disconnected", () => {
          slot.inputSource = null;
          slot.selectPressed = false;
          slot.squeezePressed = false;
          slot.gamepadSqueezePressed = false;
          slot.ray.visible = false;
          slot.focusCue.visible = false;
          slot.ray.scale.z = 1;
          this.syncCapabilities();
        });
        controller.addEventListener("selectstart", () => {
          slot.selectPressed = true;
          this.handleControllerSelectStart(slot);
        });
        controller.addEventListener("selectend", () => {
          slot.selectPressed = false;
          this.viewerManager?.stopModelInteraction();
        });
        controller.addEventListener("squeezestart", () => {
          slot.squeezePressed = true;
        });
        controller.addEventListener("squeezeend", () => {
          this.updateTeleportTarget();
          slot.squeezePressed = false;
          slot.gamepadSqueezePressed = false;
          this.commitTeleport();
        });

        this.controllers.push(slot);
      }
    }

    this.attachControllers();
  }

  private attachControllers() {
    if (!appData.spaceScene) {
      return;
    }

    this.controllers.forEach((slot) => {
      if (slot.controller.parent !== appData.spaceScene) {
        appData.spaceScene.add(slot.controller);
      }
      if (slot.focusCue.parent !== appData.spaceScene) {
        appData.spaceScene.add(slot.focusCue);
      }
    });
  }

  private detachControllers() {
    if (!appData.spaceScene) {
      return;
    }

    this.controllers.forEach((slot) => {
      slot.ray.visible = false;
      slot.ray.scale.z = 1;
      slot.focusCue.visible = false;
      if (slot.controller.parent === appData.spaceScene) {
        appData.spaceScene.remove(slot.controller);
      }
      if (slot.focusCue.parent === appData.spaceScene) {
        appData.spaceScene.remove(slot.focusCue);
      }
    });
  }

  private ensureSessionSelectListener() {
    const session = this.renderer.xr.getSession();
    if (!session || this.sessionSelectHandler) {
      return;
    }

    this.sessionSelectHandler = (event: XRInputSourceEvent) => {
      if (event.inputSource?.handedness) {
        return;
      }
      this.handleReticleSelect();
    };

    session.addEventListener("select", this.sessionSelectHandler);
  }

  private cleanupSessionSelectListener() {
    const session = this.renderer.xr.getSession();
    if (session && this.sessionSelectHandler) {
      session.removeEventListener("select", this.sessionSelectHandler);
    }
    this.sessionSelectHandler = null;
  }

  private ensureReticle() {
    if (this.reticle || !appData.spaceScene) {
      return;
    }

    this.reticle = createReticle();
    appData.spaceScene.add(this.reticle);
  }

  private removeReticle() {
    if (!this.reticle || !appData.spaceScene) {
      return;
    }

    appData.spaceScene.remove(this.reticle);
    this.reticle.geometry.dispose();
    (this.reticle.material as THREE.Material).dispose();
    this.reticle = null;
  }

  private ensureTeleportMarker() {
    if (this.teleportMarker || !appData.spaceScene) {
      return;
    }

    this.teleportMarker = createTeleportMarker();
    appData.spaceScene.add(this.teleportMarker);
  }

  private hideTeleportMarker() {
    if (!this.teleportMarker) {
      return;
    }
    this.teleportMarker.visible = false;
  }

  private ensureExitVRButton() {
    if (this.exitVRButton || !appData.spaceScene) {
      return;
    }

    this.exitVRButton = createExitVRButton();
    appData.spaceScene.add(this.exitVRButton);
  }

  private hideExitVRButton() {
    if (!this.exitVRButton) {
      return;
    }
    this.exitVRButton.visible = false;
  }

  private removeExitVRButton() {
    if (!this.exitVRButton) {
      return;
    }

    this.exitVRButton.parent?.remove(this.exitVRButton);
    this.exitVRButton.geometry.dispose();
    const materials = Array.isArray(this.exitVRButton.material)
      ? this.exitVRButton.material
      : [this.exitVRButton.material];
    materials.forEach((material) => {
      const map = (material as THREE.MeshBasicMaterial).map;
      map?.dispose?.();
      material.dispose?.();
    });
    this.exitVRButton = null;
  }

  private syncCapabilities() {
    const connectedControllers = this.controllers.filter((controller) => controller.inputSource);
    const hasControllers = connectedControllers.length > 0;
    const hasMovementAxes = connectedControllers.some((controller) => {
      const axes = controller.inputSource?.gamepad?.axes || [];
      return axes.length >= 2;
    });
    const interactionMode: VRInteractionMode = hasControllers ? "controller" : "reticle";
    const locomotionMode: VRLocomotionMode = hasControllers && hasMovementAxes && this.sessionState.runtime === "browse"
      ? "smooth"
      : "stationary";

    this.sessionState.isPresenting = this.isVRPresenting();
    this.sessionState.hasControllers = hasControllers;
    this.sessionState.hasMovementAxes = hasMovementAxes;
    this.sessionState.interactionMode = interactionMode;
    this.sessionState.locomotionMode = this.sessionState.runtime === "viewer" ? "stationary" : locomotionMode;
    this.sessionState.teleportEnabled = hasControllers && Boolean(this.physicsService?.navmesh) && this.sessionState.runtime === "browse";
    this.sessionState.viewer = this.viewerManager?.getState() || this.sessionState.viewer;
    this.controllers.forEach((controller) => {
      controller.ray.visible = Boolean(controller.inputSource) && interactionMode === "controller";
      if (!controller.ray.visible) {
        controller.ray.scale.z = 1;
        controller.focusCue.visible = false;
      }
    });
  }

  private syncControllerButtonStates() {
    this.controllers.forEach((slot) => {
      const buttons = slot.inputSource?.gamepad?.buttons || [];

      const gripButton = buttons[1];
      if (!gripButton) return;

      const pressed = Boolean(gripButton.pressed);
      if (pressed && !slot.gamepadSqueezePressed) {
        slot.squeezePressed = true;
      } else if (!pressed && slot.gamepadSqueezePressed) {
        this.updateTeleportTarget();
        slot.squeezePressed = false;
        this.commitTeleport();
      }
      slot.gamepadSqueezePressed = pressed;
    });
  }

  private updateReticle() {
    if (!this.reticle) {
      return;
    }

    const cameraPosition = new THREE.Vector3();
    const forward = new THREE.Vector3();
    appData.camera.getWorldPosition(cameraPosition);
    appData.camera.getWorldDirection(forward);
    const targetPosition = cameraPosition.clone().add(forward.multiplyScalar(2.5));
    this.reticle.position.copy(targetPosition);
    this.reticle.quaternion.copy(appData.camera.quaternion);
    this.reticle.visible = this.sessionState.interactionMode === "reticle";
  }

  private updateExitVRButton() {
    if (!this.exitVRButton || !appData.camera) {
      return;
    }

    if ((this.sessionState.runtime !== "browse" && this.sessionState.runtime !== "viewer") || !this.isVRPresenting()) {
      this.hideExitVRButton();
      return;
    }

    const cameraPosition = new THREE.Vector3();
    const forward = new THREE.Vector3();
    const cameraQuaternion = appData.camera.getWorldQuaternion(new THREE.Quaternion());

    appData.camera.getWorldPosition(cameraPosition);
    appData.camera.getWorldDirection(forward);
    forward.copy(resolveHorizontalForward(forward, cameraQuaternion));

    this.exitVRButton.position.copy(cameraPosition)
      .add(forward.clone().multiplyScalar(EXIT_VR_BUTTON_FORWARD_OFFSET));
    const floorY = this.resolveExitVRButtonFloorY(cameraPosition);
    this.exitVRButton.position.y = floorY + EXIT_VR_BUTTON_FLOOR_OFFSET;
    const yaw = Math.atan2(forward.x, forward.z);
    this.exitVRButton.rotation.set(-Math.PI / 2, 0, Math.PI - yaw);
    this.exitVRButton.visible = true;
  }

  private resolveExitVRButtonFloorY(cameraWorldPosition: THREE.Vector3) {
    const cameraHeight = getConfiguredRuntimeCameraHeight(appData);
    const headsetFloorY = Number.isFinite(cameraWorldPosition.y) && Number.isFinite(cameraHeight)
      ? cameraWorldPosition.y - cameraHeight
      : null;
    const teleportSurfaceY = this.getCurrentTeleportSurfaceY();

    if (headsetFloorY !== null && teleportSurfaceY !== null) {
      return Math.min(headsetFloorY, teleportSurfaceY);
    }
    return headsetFloorY ?? teleportSurfaceY ?? cameraWorldPosition.y - 1.6;
  }

  private handleMovement() {
    if (!this.sessionState.hasControllers || this.sessionState.runtime !== "browse") {
      return;
    }

    const leftController = this.getControllerByHandedness("left") || this.controllers[0];
    if (!leftController?.inputSource?.gamepad) {
      return;
    }

    const { horizontal, vertical } = this.getPrimaryAxes(leftController.inputSource.gamepad.axes || []);
    if (Math.abs(vertical) >= AXIS_DEADZONE) {
      appData.renderService.cameraService.controlMoveForward(-vertical);
    }
    if (Math.abs(horizontal) >= AXIS_DEADZONE) {
      appData.renderService.cameraService.controlMoveRight(horizontal);
    }
  }

  private handleMetadataPanelScroll(delta: number) {
    if (!this.metadataPanelManager?.isOpen?.() || this.sessionState.runtime !== "browse") {
      return false;
    }

    const metadataController = this.getControllerByHandedness("left") || this.controllers[0];
    if (!metadataController?.inputSource?.gamepad) {
      return false;
    }

    const { vertical } = this.getPrimaryAxes(metadataController.inputSource.gamepad.axes || []);
    if (Math.abs(vertical) < AXIS_DEADZONE) {
      return false;
    }

    this.metadataPanelManager.scrollBy(vertical * METADATA_PANEL_SCROLL_SPEED * delta);
    return true;
  }

  private updateControllerFocusCues() {
    if (this.sessionState.interactionMode !== "controller") {
      this.hideControllerFocusCues();
      return;
    }

    this.controllers.forEach((slot) => {
      if (!slot.inputSource) {
        slot.focusCue.visible = false;
        slot.ray.scale.z = 1;
        return;
      }

      const origin = new THREE.Vector3();
      const direction = new THREE.Vector3();
      slot.controller.getWorldPosition(origin);
      direction.set(0, 0, -1).applyQuaternion(slot.controller.getWorldQuaternion(new THREE.Quaternion())).normalize();

      let hitPoint: THREE.Vector3 | null = null;
      let hitDistance: number | null = null;
      const exitHit = this.raycastExitVRButton(origin, direction);
      if (exitHit) {
        hitPoint = exitHit.point;
        hitDistance = exitHit.distance;
      } else if (this.sessionState.runtime === "viewer" && this.viewerManager) {
        const viewerHit = this.viewerManager.raycast(origin, direction);
        hitPoint = viewerHit?.point || null;
        hitDistance = viewerHit?.distance ?? null;
      } else {
        const metadataHit = this.metadataPanelManager?.raycast(origin, direction);
        if (metadataHit) {
          hitPoint = metadataHit.point || null;
          hitDistance = metadataHit.distance ?? null;
        }
      }

      if (!hitPoint && this.sessionState.runtime !== "viewer") {
        try {
          const resolvedTarget = this.raycasterHandler?.resolveTargetFromRay(origin, direction);
          hitPoint = resolvedTarget?.point || null;
          hitDistance = resolvedTarget?.distance ?? null;
        } catch {
          hitPoint = null;
          hitDistance = null;
        }
      }

      if (hitPoint && hitDistance != null) {
        slot.focusCue.position.copy(hitPoint);
        slot.focusCue.visible = true;
        slot.ray.scale.z = Math.max(0.05, Math.min(1.5, hitDistance / CONTROLLER_RAY_LENGTH));
        return;
      }

      slot.focusCue.visible = false;
      slot.ray.scale.z = 1;
    });
  }

  private raycastExitVRButton(origin: THREE.Vector3, direction: THREE.Vector3) {
    if (!this.exitVRButton?.visible) {
      return null;
    }

    const raycaster = new THREE.Raycaster(origin, direction.clone().normalize(), 0, CONTROLLER_RAY_LENGTH);
    const hit = raycaster.intersectObject(this.exitVRButton, false)[0];
    if (!hit) {
      return null;
    }

    return {
      point: hit.point.clone(),
      distance: hit.distance,
    };
  }

  private requestExitVR() {
    this.hideExitVRButton();
    void this.endVRSession();
  }

  private hideControllerFocusCues() {
    this.controllers.forEach((slot) => {
      slot.focusCue.visible = false;
      slot.ray.scale.z = 1;
    });
  }

  private handleSnapTurn() {
    if (!this.sessionState.hasControllers || this.sessionState.runtime !== "browse") {
      return;
    }

    const rightController = this.getControllerByHandedness("right") || this.controllers[1];
    if (!rightController?.inputSource?.gamepad) {
      return;
    }

    const { horizontal } = this.getPrimaryAxes(rightController.inputSource.gamepad.axes || []);
    const now = Date.now();
    if (horizontal >= AXIS_DEADZONE && now - rightController.lastSnapAt >= SNAP_TURN_COOLDOWN_MS) {
      appData.renderService.cameraService.controlVRSnapTurn(1);
      rightController.lastSnapAt = now;
    } else if (horizontal <= -AXIS_DEADZONE && now - rightController.lastSnapAt >= SNAP_TURN_COOLDOWN_MS) {
      appData.renderService.cameraService.controlVRSnapTurn(-1);
      rightController.lastSnapAt = now;
    }
  }

  private updateTeleportTarget() {
    const teleportController = this.getTeleportController();
    if (!this.sessionState.teleportEnabled || !this.teleportMarker) {
      this.hideTeleportMarker();
      this.lastTeleportTarget = null;
      return;
    }

    if (!teleportController || !teleportController.squeezePressed) {
      this.hideTeleportMarker();
      this.lastTeleportTarget = null;
      return;
    }

    const controllerPosition = new THREE.Vector3();
    const controllerDirection = new THREE.Vector3();
    teleportController.controller.getWorldPosition(controllerPosition);
    controllerDirection.set(0, 0, -1).applyQuaternion(teleportController.controller.getWorldQuaternion(new THREE.Quaternion())).normalize();

    const raycaster = new THREE.Raycaster(controllerPosition, controllerDirection, 0, TELEPORT_MAX_TARGET_DISTANCE);
    const navmesh = this.physicsService?.navmesh;
    if (!navmesh) {
      this.hideTeleportMarker();
      this.lastTeleportTarget = null;
      return;
    }

    const intersects = this.intersectTeleportNavmesh(raycaster, navmesh);
    const directHit = this.resolveWalkableTeleportHit(intersects);
    const projectedHit = directHit
      ? null
      : this.resolveProjectedTeleportHit(controllerPosition, controllerDirection, navmesh);
    const octreeHit = directHit || projectedHit?.hit
      ? null
      : this.resolveProjectedOctreeTeleportHit(controllerPosition, controllerDirection, navmesh);
    const hit = directHit || projectedHit?.hit || octreeHit?.hit || null;
    const blockerRaycaster = projectedHit?.blockerRaycaster || octreeHit?.blockerRaycaster || raycaster;
    if (!hit) {
      this.hideTeleportMarker();
      this.lastTeleportTarget = null;
      return;
    }

    if (this.isTeleportBlockedBeforeHit(blockerRaycaster, hit)) {
      this.hideTeleportMarker();
      this.lastTeleportTarget = null;
      return;
    }

    const target = this.getSanitizedTeleportTarget(hit.point);
    this.lastTeleportTarget = target;
    this.teleportMarker.position.copy(target);
    this.teleportMarker.visible = true;
  }

  private intersectTeleportNavmesh(raycaster: THREE.Raycaster, navmesh: THREE.Object3D) {
    const materialSides: Array<{ material: THREE.Material; side: number }> = [];

    navmesh.traverse((child) => {
      const material = (child as THREE.Mesh).material;
      if (!material) return;

      const materials = Array.isArray(material) ? material : [material];
      materials.forEach((entry) => {
        if (!entry) return;
        materialSides.push({ material: entry, side: entry.side });
        entry.side = THREE.DoubleSide;
      });
    });

    try {
      return raycaster.intersectObject(navmesh, true);
    } finally {
      materialSides.forEach(({ material, side }) => {
        (material as any).side = side;
      });
    }
  }

  private resolveProjectedTeleportHit(
    controllerPosition: THREE.Vector3,
    controllerDirection: THREE.Vector3,
    navmesh: THREE.Object3D
  ) {
    const currentSurfaceY = this.getCurrentTeleportSurfaceY();
    const targetDistances = this.getTeleportFallbackDistances(
      this.getProjectedTeleportDistance(controllerPosition, controllerDirection, currentSurfaceY)
    );

    for (const targetDistance of targetDistances) {
      const projectedPoint = controllerPosition.clone().add(
        controllerDirection.clone().multiplyScalar(targetDistance)
      );
      const downcastTopY = Math.max(
        projectedPoint.y,
        controllerPosition.y,
        currentSurfaceY ?? controllerPosition.y
      ) + TELEPORT_DOWNCAST_HEIGHT;
      const downcastOrigin = new THREE.Vector3(projectedPoint.x, downcastTopY, projectedPoint.z);
      const downcastRange = TELEPORT_DOWNCAST_HEIGHT + TELEPORT_DOWNCAST_DEPTH;
      const downcastRaycaster = new THREE.Raycaster(
        downcastOrigin,
        new THREE.Vector3(0, -1, 0),
        0,
        downcastRange
      );
      const downcastIntersections = this.intersectTeleportNavmesh(downcastRaycaster, navmesh);
      const hit = this.resolveWalkableTeleportHit(downcastIntersections);
      if (!hit) {
        continue;
      }

      const blockerDirection = hit.point.clone().sub(controllerPosition);
      const blockerDistance = blockerDirection.length();
      if (blockerDistance <= 0.01) {
        continue;
      }

      const blockerRaycaster = new THREE.Raycaster(
        controllerPosition,
        blockerDirection.normalize(),
        0,
        blockerDistance
      );

      return {
        hit,
        blockerRaycaster,
        navmeshIntersections: downcastIntersections.length,
        projectedPoint,
      };
    }

    return null;
  }

  private getProjectedTeleportDistance(
    controllerPosition: THREE.Vector3,
    controllerDirection: THREE.Vector3,
    currentSurfaceY: number | null
  ) {
    if (currentSurfaceY !== null && controllerDirection.y < -0.05) {
      const distanceToSurface = (currentSurfaceY - controllerPosition.y) / controllerDirection.y;
      if (Number.isFinite(distanceToSurface) && distanceToSurface > 0) {
        return THREE.MathUtils.clamp(distanceToSurface, 0.8, TELEPORT_MAX_TARGET_DISTANCE);
      }
    }

    return TELEPORT_PROJECTED_TARGET_DISTANCE;
  }

  private getTeleportFallbackDistances(primaryDistance: number) {
    return Array.from(new Set([
      Number(primaryDistance.toFixed(2)),
      ...TELEPORT_FALLBACK_DISTANCES,
    ]))
      .filter((distance) => Number.isFinite(distance) && distance > 0)
      .sort((first, second) => first - second);
  }

  private resolveProjectedOctreeTeleportHit(
    controllerPosition: THREE.Vector3,
    controllerDirection: THREE.Vector3,
    navmesh: THREE.Object3D
  ) {
    if (!this.physicsService?.worldOctree?.rayIntersect) {
      return null;
    }

    const localControllerPosition = this.toGallerySpacePosition(controllerPosition);
    const localControllerEnd = this.toGallerySpacePosition(
      controllerPosition.clone().add(controllerDirection)
    );
    const localControllerDirection = localControllerEnd.sub(localControllerPosition).normalize();
    const currentSurfaceWorldY = this.getCurrentTeleportSurfaceY();
    const currentSurfaceLocalY = currentSurfaceWorldY === null
      ? null
      : this.toGallerySpacePosition(new THREE.Vector3(0, currentSurfaceWorldY, 0)).y;
    const targetDistances = this.getTeleportFallbackDistances(
      this.getProjectedTeleportDistance(localControllerPosition, localControllerDirection, currentSurfaceLocalY)
    );

    for (const targetDistance of targetDistances) {
      const point = localControllerPosition.clone().add(
        localControllerDirection.clone().multiplyScalar(targetDistance)
      );
      const downcastTopY = Math.max(
        point.y,
        localControllerPosition.y,
        currentSurfaceLocalY ?? localControllerPosition.y
      ) + TELEPORT_DOWNCAST_HEIGHT;
      const ray = new THREE.Ray(
        new THREE.Vector3(point.x, downcastTopY, point.z),
        new THREE.Vector3(0, -1, 0)
      );
      const rawHit = this.physicsService.worldOctree.rayIntersect(ray);

      if (!rawHit?.position || rawHit.distance > TELEPORT_DOWNCAST_HEIGHT + TELEPORT_DOWNCAST_DEPTH) {
        continue;
      }

      const worldNormal = rawHit.triangle?.getNormal?.(new THREE.Vector3()) || null;
      if (!worldNormal || Math.abs(worldNormal.dot(WORLD_UP)) < TELEPORT_UP_NORMAL_DOT_MIN) {
        continue;
      }

      const worldHitPosition = this.toWorldSpacePosition(rawHit.position.clone());
      if (!this.isWithinCurrentTeleportSurface(worldHitPosition)) {
        continue;
      }

      const blockerDirection = worldHitPosition.clone().sub(controllerPosition);
      const blockerDistance = blockerDirection.length();
      if (blockerDistance <= 0.01) {
        continue;
      }

      const blockerRaycaster = new THREE.Raycaster(
        controllerPosition,
        blockerDirection.normalize(),
        0,
        blockerDistance
      );

      return {
        hit: {
          point: worldHitPosition,
          distance: blockerDistance,
          object: navmesh,
          face: { normal: worldNormal },
        } as THREE.Intersection,
        blockerRaycaster,
        rawHit,
      };
    }

    return null;
  }

  private resolveWalkableTeleportHit(intersects: THREE.Intersection[]) {
    for (const hit of intersects) {
      if (this.isWalkableTeleportHit(hit)) {
        return hit;
      }

      // A closer non-walkable hit means the user is pointing at a wall, ceiling,
      // underside, or other blocker. Do not allow teleporting through it.
      return null;
    }

    return null;
  }

  private isWalkableTeleportHit(hit: THREE.Intersection) {
    if (!hit?.face || !hit.object) {
      return false;
    }

    if (!this.isWithinCurrentTeleportSurface(hit.point)) {
      return false;
    }

    return this.hasWalkableTeleportNormal(hit);
  }

  private hasWalkableTeleportNormal(hit: THREE.Intersection) {
    if (!hit?.face || !hit.object) {
      return false;
    }

    const normalMatrix = new THREE.Matrix3().getNormalMatrix(hit.object.matrixWorld);
    const worldNormal = hit.face.normal.clone().applyMatrix3(normalMatrix).normalize();
    return Math.abs(worldNormal.dot(WORLD_UP)) >= TELEPORT_UP_NORMAL_DOT_MIN;
  }

  private isWithinCurrentTeleportSurface(point: THREE.Vector3) {
    const currentSurfaceY = this.getCurrentTeleportSurfaceY();
    if (currentSurfaceY === null) {
      return true;
    }

    const deltaY = point.y - currentSurfaceY;
    return deltaY >= -TELEPORT_MAX_SURFACE_DROP && deltaY <= TELEPORT_MAX_SURFACE_RISE;
  }

  private getCurrentTeleportSurfaceY() {
    return this.currentTeleportSurfaceY ?? this.resolveTeleportSurfaceYFromCurrentPose();
  }

  private getSanitizedTeleportTarget(point: THREE.Vector3) {
    return point.clone();
  }

  private toGallerySpacePosition(worldPosition: THREE.Vector3) {
    if (!this.vrRig) {
      return worldPosition.clone();
    }
    return this.vrRig.worldToLocal(worldPosition.clone());
  }

  private toWorldSpacePosition(galleryPosition: THREE.Vector3) {
    if (!this.vrRig) {
      return galleryPosition.clone();
    }
    return this.vrRig.localToWorld(galleryPosition.clone());
  }

  private isTeleportBlockedBeforeHit(raycaster: THREE.Raycaster, navmeshHit: THREE.Intersection) {
    const blockers = this.getTeleportBlockers(navmeshHit.object);
    if (blockers.length === 0) {
      return false;
    }

    return raycaster
      .intersectObjects(blockers, true)
      .some((hit) => hit.distance < navmeshHit.distance - TELEPORT_BLOCKER_DISTANCE_EPSILON);
  }

  private getTeleportBlockers(navmeshHitObject: THREE.Object3D) {
    const navmeshRoot = this.physicsService?.navmesh || navmeshHitObject;
    if (
      !this.teleportBlockersDirty &&
      this.teleportBlockersNavmeshRoot === navmeshRoot
    ) {
      return this.teleportBlockersCache;
    }

    const blockers: THREE.Object3D[] = [];
    const seen = new Set<THREE.Object3D>();

    const collectFromScene = (scene: THREE.Scene | null | undefined) => {
      scene?.traverse((child) => {
        if (!seen.has(child) && this.isTeleportBlocker(child, navmeshRoot)) {
          seen.add(child);
          blockers.push(child);
        }
      });
    };

    collectFromScene(appData.spaceScene);
    collectFromScene(appData.artworksScene);

    this.teleportBlockersCache = blockers;
    this.teleportBlockersNavmeshRoot = navmeshRoot;
    this.teleportBlockersDirty = false;
    return blockers;
  }

  private isTeleportBlocker(object: THREE.Object3D, navmeshRoot: THREE.Object3D) {
    if (!object.visible) return false;
    if (object === navmeshRoot || this.isDescendantOf(object, navmeshRoot)) return false;
    if (object === this.teleportMarker || object === this.reticle || object === this.exitVRButton) return false;
    if (this.controllers.some((slot) => object === slot.focusCue || object === slot.ray || object === slot.controller)) return false;
    if (!(object as THREE.Mesh).isMesh || !(object as THREE.Mesh).geometry) return false;

    const name = object.name || "";
    if (
      name.includes("sky") ||
      name.includes("TransformControls") ||
      name.includes("entryTo_") ||
      name.includes("remove_") ||
      name.includes("cover_Space-")
    ) {
      return false;
    }

    return true;
  }

  private isDescendantOf(object: THREE.Object3D, possibleAncestor: THREE.Object3D) {
    let current: THREE.Object3D | null = object.parent;
    while (current) {
      if (current === possibleAncestor) {
        return true;
      }
      current = current.parent;
    }
    return false;
  }

  private commitTeleport() {
    if (!this.lastTeleportTarget || !this.sessionState.teleportEnabled) {
      return;
    }
    const targetWorld = this.getSanitizedTeleportTarget(this.lastTeleportTarget);
    const targetGallery = this.toGallerySpacePosition(targetWorld);
    this.currentTeleportSurfaceY = targetWorld.y;
    appData.renderService.cameraService.teleportVRTo(targetGallery);
    this.hideTeleportMarker();
    this.lastTeleportTarget = null;
  }

  private handleControllerSelectStart(controller: ControllerSlot) {
    const origin = new THREE.Vector3();
    const direction = new THREE.Vector3();
    controller.controller.getWorldPosition(origin);
    direction.set(0, 0, -1).applyQuaternion(controller.controller.getWorldQuaternion(new THREE.Quaternion())).normalize();

    if (this.raycastExitVRButton(origin, direction)) {
      this.requestExitVR();
      return;
    }

    if (this.sessionState.runtime === "viewer" && this.viewerManager) {
      const viewerHit = this.viewerManager.raycast(origin, direction);
      if (!viewerHit) {
        return;
      }

      if (viewerHit.action) {
        if (viewerHit.action === "close") {
          this.closeViewer();
          return;
        }
        this.viewerManager.handleAction(viewerHit.action);
        return;
      }

      if (viewerHit.hitsContent && this.sessionState.viewer.kind === "3dmodel" && this.sessionState.interactionMode === "controller") {
        this.viewerManager.beginModelInteraction(controller.controller);
      }
      return;
    }

    if (this.isMetadataController(controller)) {
      const metadataHit = this.metadataPanelManager?.raycast(origin, direction);
      if (metadataHit?.action === "close") {
        this.metadataPanelManager?.close();
        return;
      }

      const resolvedTarget = this.raycasterHandler?.resolveTargetFromRay(origin, direction);
      if (resolvedTarget?.artworkWrapper) {
        this.metadataPanelManager?.open(resolvedTarget.artworkWrapper);
        return;
      }

      this.metadataPanelManager?.close();
      return;
    }

    const resolvedTarget = this.raycasterHandler?.resolveTargetFromRay(origin, direction);
    if (resolvedTarget) {
      if (this.isArtworkViewerTarget(resolvedTarget.kind)) {
        this.pendingViewerSourceObject = this.resolveViewerSourceRoot(resolvedTarget.object);
      }
      this.raycasterHandler?.dispatchResolvedTarget(resolvedTarget, "xr-controller");
    }
  }

  private handleReticleSelect() {
    const origin = new THREE.Vector3();
    const direction = new THREE.Vector3();
    appData.camera.getWorldPosition(origin);
    appData.camera.getWorldDirection(direction);

    if (this.raycastExitVRButton(origin, direction)) {
      this.requestExitVR();
      return;
    }

    if (this.sessionState.runtime === "viewer" && this.viewerManager) {
      const viewerHit = this.viewerManager.raycast(origin, direction);
      if (viewerHit?.action) {
        if (viewerHit.action === "close") {
          this.closeViewer();
          return;
        }
        this.viewerManager.handleAction(viewerHit.action);
      }
      return;
    }

    const resolvedTarget = this.raycasterHandler?.resolveTargetFromRay(origin, direction);
    if (resolvedTarget) {
      this.raycasterHandler?.dispatchResolvedTarget(resolvedTarget, "xr-reticle");
    }
  }

  private getScaleInput() {
    const rightController = this.getControllerByHandedness("right") || this.controllers[1];
    const axes = rightController?.inputSource?.gamepad?.axes || [];
    const { vertical } = this.getPrimaryAxes(axes);
    return Math.abs(vertical) >= AXIS_DEADZONE ? -vertical : 0;
  }

  private getControllerByHandedness(handedness: XRHandedness) {
    return this.controllers.find((controller) => controller.inputSource?.handedness === handedness) || null;
  }

  private getTeleportController() {
    const rightController = this.getControllerByHandedness("right");
    if (rightController?.squeezePressed) return rightController;

    return this.controllers.find((controller) => controller.squeezePressed) || rightController || this.controllers[0] || null;
  }

  private isMetadataController(controller: ControllerSlot) {
    return controller.inputSource?.handedness === "left";
  }

  private isArtworkViewerTarget(kind: string) {
    return kind === "artwork" || kind === "diorama" || kind === "sound";
  }

  private resolveViewerSourceRoot(object: THREE.Object3D | null | undefined) {
    if (!object) return null;

    let current: THREE.Object3D | null = object;
    while (
      current?.parent &&
      current.parent !== appData.artworksScene &&
      current.parent !== appData.spaceScene
    ) {
      current = current.parent;
    }
    return current || object;
  }

  private hidePendingViewerSourceObject() {
    this.restoreHiddenViewerSourceObject();

    const object = this.pendingViewerSourceObject;
    this.pendingViewerSourceObject = null;
    if (!object) return;

    this.hiddenViewerSourceObject = {
      object,
      visible: object.visible,
    };
    object.visible = false;
  }

  private restoreHiddenViewerSourceObject() {
    if (!this.hiddenViewerSourceObject) return;

    this.hiddenViewerSourceObject.object.visible = this.hiddenViewerSourceObject.visible;
    this.hiddenViewerSourceObject = null;
  }

  private getPrimaryAxes(axes: readonly number[]) {
    if (axes.length >= 4) {
      const firstPair = {
        horizontal: axes[0] || 0,
        vertical: axes[1] || 0,
      };
      const lastPair = {
        horizontal: axes[axes.length - 2] || 0,
        vertical: axes[axes.length - 1] || 0,
      };
      const firstMagnitude = Math.hypot(firstPair.horizontal, firstPair.vertical);
      const lastMagnitude = Math.hypot(lastPair.horizontal, lastPair.vertical);

      if (lastMagnitude > firstMagnitude) {
        return lastPair;
      }
      return firstPair;
    }
    if (axes.length >= 2) {
      return {
        horizontal: axes[0] || 0,
        vertical: axes[1] || 0,
      };
    }
    return { horizontal: 0, vertical: 0 };
  }

  private failViewer(reason: string) {
    console.warn("VRManager: viewer failure", reason);
    this.failureEventCallback(reason);
    this.viewerManager?.close();
    this.restoreHiddenViewerSourceObject();
    this.transitionRuntime("browse");
    this.sessionState.viewer = {
      kind: null,
      status: "idle",
      interactionEnabled: false,
      error: reason,
    };
    this.syncCapabilities();
    this.notifySessionState();
  }

  private failRuntime(reason: string) {
    console.warn("VRManager: runtime failure", reason, this.performanceMonitor.getDebugState());
    this.failureEventCallback(reason);
    this.transitionRuntime("failed");
    this.sessionState.viewer = {
      kind: null,
      status: "idle",
      interactionEnabled: false,
      error: reason,
    };
    this.notifySessionState();
  }

  private transitionRuntime(nextRuntime: VRSessionState["runtime"]) {
    const currentRuntime = this.sessionState.runtime;
    if (currentRuntime === nextRuntime) {
      return true;
    }

    const allowedTransitions: Record<VRSessionState["runtime"], VRSessionState["runtime"][]> = {
      desktop: ["entering"],
      entering: ["browse", "failed", "desktop"],
      browse: ["viewer", "exiting", "failed"],
      viewer: ["browse", "exiting", "failed"],
      exiting: ["desktop", "failed"],
      failed: ["desktop", "entering", "exiting"],
    };

    if (!allowedTransitions[currentRuntime].includes(nextRuntime)) {
      return false;
    }

    this.sessionState.runtime = nextRuntime;
    return true;
  }

  private notifySessionState() {
    this.sessionEventCallback(this.getVRSessionState());
  }

  public updateVRCursor(_mouseX: number, _mouseY: number) {
    // Desktop mouse-derived VR cursor has been replaced by native controller/reticle input.
  }

  public kill() {
    const session = this.renderer?.xr?.getSession?.();
    void session?.end?.().catch(() => undefined);

    this.viewerManager?.close();
    this.viewerManager = null;
    this.metadataPanelManager?.close();
    this.metadataPanelManager = null;
    this.restoreHiddenViewerSourceObject();
    this.pendingViewerSourceObject = null;
    this.removeReticle();
    this.removeExitVRButton();
    if (this.teleportMarker && appData.spaceScene) {
      appData.spaceScene.remove(this.teleportMarker);
      this.teleportMarker.geometry.dispose();
      (this.teleportMarker.material as THREE.Material).dispose();
      this.teleportMarker = null;
    }

    if (this.sessionStartHandler) {
      this.renderer.xr.removeEventListener("sessionstart", this.sessionStartHandler);
    }
    if (this.sessionEndHandler) {
      this.renderer.xr.removeEventListener("sessionend", this.sessionEndHandler);
    }
    this.cleanupSessionSelectListener();
    this.hideControllerFocusCues();
    this.detachControllers();
    this.currentTeleportSurfaceY = null;
    this.clearTeleportBlockersCache();

    this.controllers.forEach((controller) => {
      controller.controller.remove(controller.ray);
      controller.focusCue.geometry.dispose();
      (controller.focusCue.material as THREE.Material).dispose();
      controller.ray.geometry.dispose();
      (controller.ray.material as THREE.Material).dispose();
    });
    this.controllers = [];
    this.listenersBound = false;

    if (this.vrButton && this.vrButton.parentElement) {
      this.vrButton.parentElement.removeChild(this.vrButton);
      this.vrButton = null;
    }
  }

  private resolveTeleportSurfaceYFromCurrentPose() {
    const navmeshSurfaceY = this.resolveTeleportSurfaceYFromNavmesh();
    if (navmeshSurfaceY !== null) {
      return navmeshSurfaceY;
    }

    const cameraHeight = getConfiguredRuntimeCameraHeight(appData);
    const desktopCameraY = this.desktopCameraState?.position?.y;
    if (Number.isFinite(desktopCameraY)) {
      return desktopCameraY - cameraHeight;
    }

    const colliderStartY = this.physicsService?.playerCollider?.start?.y;
    const colliderEndY = this.physicsService?.playerCollider?.end?.y;
    if (Number.isFinite(colliderStartY) && Number.isFinite(colliderEndY)) {
      const colliderHeight = Math.abs(colliderEndY - colliderStartY);
      if (colliderHeight > 0.01) {
        return Math.min(colliderStartY, colliderEndY);
      }
      return colliderEndY - cameraHeight;
    }

    const cameraWorldPosition = new THREE.Vector3();
    appData.camera?.getWorldPosition?.(cameraWorldPosition);
    if (Number.isFinite(cameraWorldPosition.y)) {
      return cameraWorldPosition.y - cameraHeight;
    }

    return null;
  }

  private resolveTeleportSurfaceYFromNavmesh() {
    const navmesh = this.physicsService?.navmesh;
    if (!navmesh || !appData.camera) {
      return null;
    }

    const origin = new THREE.Vector3();
    appData.camera.getWorldPosition(origin);
    origin.y += 0.05;
    const raycaster = new THREE.Raycaster(origin, new THREE.Vector3(0, -1, 0));
    const hit = raycaster.intersectObject(navmesh, true).find((candidate) => {
      return this.hasWalkableTeleportNormal(candidate);
    });
    return Number.isFinite(hit?.point?.y) ? hit!.point.y : null;
  }

  private markTeleportBlockersDirty() {
    this.teleportBlockersDirty = true;
    this.teleportBlockersNavmeshRoot = null;
    this.teleportBlockersCache = [];
  }

  private clearTeleportBlockersCache() {
    this.teleportBlockersDirty = true;
    this.teleportBlockersNavmeshRoot = null;
    this.teleportBlockersCache = [];
  }
}
